import { useState } from "react";
import CustomerDetailsSkeleton from "../../../Component/Loader/CustomerDetailsSkeleton";
import { useCustomerDetails } from "./useCustomerDetails";
import { BASE_URL } from "../../../config/api";
import toast from "react-hot-toast";

export default function CustomerDetails() {
  const { customer, devices, loading, error } = useCustomerDetails();
  const [generatingImei, setGeneratingImei] = useState(null);

  const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");

const isRetailerMobile =
  window.innerWidth < 640 && authUser?.role === 5;

  const handleGenerateCertificate = async (imei1) => {
  try {
    setGeneratingImei(imei1);

    const res = await fetch(
      `${BASE_URL}warranty/generate-wcertificate`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imei1 }),
      }
    );

    const data = await res.json();

    if (!res.ok || data.success !== true) {
      throw new Error(data.message || "Failed to generate certificate");
    }

    toast.success("Warranty certificate generated successfully");

    // 🔁 Reload customer details to get certificate_link
    window.location.reload();
  } catch (err) {
    toast.error(err.message || "Something went wrong");
  } finally {
    setGeneratingImei(null);
  }
};

const handleDownload = async (url) => {
  const response = await fetch(url);
  const blob = await response.blob();

  const blobUrl = window.URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = "Warranty-Certificate.pdf"; // you can customize name
  document.body.appendChild(a);
  a.click();

  a.remove();
  window.URL.revokeObjectURL(blobUrl);
};


  if (loading) {
    return <CustomerDetailsSkeleton />;
  }

  if (error) {
    return (
      <p className="text-center text-red-500 text-sm py-6">
        {error}
      </p>
    );
  }

  if (!customer) return null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

      {/* ================= LEFT COLUMN ================= */}
      <div className="lg:col-span-1 space-y-6">

        {/* ===== PROFILE CARD ===== */}
        <div
            className={`
              rounded-2xl p-6 transition-all duration-300
              ${
                isRetailerMobile
                  ? "bg-white/10 backdrop-blur-2xl border border-white/30 shadow-[0_20px_50px_rgba(31,38,135,0.25)]"
                  : "bg-white border shadow-sm"
              }
            `}
          >
        <div className="flex flex-col items-center text-center">

            {/* Avatar */}
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-3xl sm:text-4xl font-bold">
            {customer.name?.charAt(0)}
            </div>

            <h2 className="mt-4 text-base sm:text-lg font-semibold text-slate-800">
            {customer.name}
            </h2>

            <p className="text-xs text-slate-500 mt-1">
            Customer Code
            </p>

            <p className="text-sm font-medium text-slate-700 break-all">
            {customer.c_code}
            </p>
        </div>
        </div>


        {/* ===== ADDRESS CARD ===== */}
        <div
          className={`
            rounded-2xl p-5 sm:p-6 transition-all duration-300
            ${
              isRetailerMobile
                ? "bg-white/10 backdrop-blur-2xl border border-white/30 shadow-[0_20px_50px_rgba(31,38,135,0.25)]"
                : "bg-white border shadow-sm"
            }
          `}
        >
          <h3 className="text-sm font-semibold text-slate-700 mb-4">
            Address
          </h3>

          <div className="grid grid-cols-2 sm:grid-cols-2 space-y-2 sm:space-y-3 text-sm">
            <Info label="City" value={customer.city} />
            <Info label="State" value={customer.state} />
            <Info label="Pincode" value={customer.pincode} />
            <Info label="Location" value={customer.location} />
          </div>
        </div>
      </div>

      {/* ================= RIGHT COLUMN ================= */}
      <div className="lg:col-span-3 space-y-6">

        {/* ===== ABOUT ===== */}
        <div
          className={`
            rounded-2xl p-6 transition-all duration-300
            ${
              isRetailerMobile
                ? "bg-white/10 backdrop-blur-2xl border border-white/30 shadow-[0_20px_50px_rgba(31,38,135,0.25)]"
                : "bg-white border shadow-sm"
            }
          `}
        >
          <h3 className="text-sm font-semibold text-slate-700 mb-4">
            About Customer
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <Info label="Mobile" value={customer.mobile} />
            <Info label="Email" value={customer.email} />
            <Info
              label="Joined On"
              value={customer.created_at?.slice(0, 10)}
            />
          </div>
        </div>

        {/* ===== DEVICES ===== */}
        <div className={`
  rounded-2xl p-4 transition-all duration-300
  ${
    isRetailerMobile
      ? "bg-white/10 backdrop-blur-xl border border-white/30 shadow-[0_15px_35px_rgba(31,38,135,0.25)]"
      : "border bg-slate-50 hover:shadow-md"
  }
`}>
          <h3 className="text-sm font-semibold text-slate-700 mb-4">
            Registered Devices ({devices.length})
          </h3>

          {devices.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">
              No devices registered
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {devices.map((d) => (
                <div
                  key={d.id}
                  className={`
                    rounded-2xl p-4 transition-all duration-300
                    ${
                      isRetailerMobile
                        ? "bg-white/10 backdrop-blur-xl border border-white/30 shadow-[0_15px_35px_rgba(31,38,135,0.25)]"
                        : "border bg-slate-50 hover:shadow-md"
                    }
                  `}
                >
                  <div className="flex gap-4 flex- sm:flex-row">

                    {/* Product Image */}
                    <img
                      src={d.product?.image}
                      alt=""
                      className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg object-contain border bg-white"
                    />

                    {/* Device Info */}
                    <div className="flex-1 space-y-2 text-sm">
                      <div>
                        <p className="font-semibold text-slate-800 whitespace-wrap">
                          {d.product_name}
                        </p>
                        <p className="text-xs text-slate-500">
                          {d.brand_name} • {d.model}
                        </p>
                      </div>

                      

                    </div>

                    
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 text-xs pt-3">
                        <Info label="IMEI 1" value={d.imei1} />
                        <Info label="IMEI 2" value={d.imei2} />
                        <Info label="Warranty Code" value={d.w_code} />
                        <Info label="Expiry Date" value={d.expiry_date} />
                        <Info label="Claims Left" value={d.available_claim} />
                      </div>

                     {/* Certificate Action */}
                    <div className="mt-3">
                      {d.certificate_link ? (
                        <a
                          href={d.certificate_link}
                          download
                          onClick={() => {
                            toast.loading("Downloading certificate...", {
                              id: "download-toast",
                            });

                            // Auto replace after short delay
                            setTimeout(() => {
                              toast.success("Download started", {
                                id: "download-toast",
                              });
                            }, 1000);
                          }}
                          className="inline-block text-xs sm:text-sm font-medium text-blue-600 hover:underline"
                        >
                          Download Warranty Certificate ⬇️
                        </a>
                      ) : (
                        <button
                          onClick={() => handleGenerateCertificate(d.imei1)}
                          disabled={generatingImei === d.imei1}
                          className="
                            inline-flex items-center gap-2
                            rounded-lg bg-blue-600 px-3 py-1.5
                            text-xs sm:text-sm font-medium text-white
                            hover:bg-blue-700 transition
                            disabled:opacity-60
                          "
                        >
                          {generatingImei === d.imei1 ? "Generating…" : "Generate Certificate"}
                        </button>
                      )}
                    </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ================= HELPERS ================= */

function Info({ label, value }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] text-slate-500">{label}</p>
      <p className="font-medium text-slate-800 text-sm break-words">
        {value || "—"}
      </p>
    </div>
  );
}
